import React, { Suspense, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls, Environment, Loader, Html, Center } from '@react-three/drei';
import { observer } from 'mobx-react-lite';

import { Edges } from '../ThreeJSCanvas/Edges';
import { Grid } from '../ThreeJSCanvas/Grid';
import { Viewcube } from '../ThreeJSCanvas/ViewCube';
import { ContactShadows } from '../ThreeJSCanvas/ContactShadows';
import { Canvas, useThree } from '@react-three/fiber';

import ThreeD from '../../../assets/svg/three-d.svg?react';
import QuickTurnBoltStore from '../../../stores/QuickTurnBoltStore';
import PortalLoading from 'components/PortalLoading';
import { useWindowSize } from '@partsbadger/utils';
import Icon from '@ant-design/icons';

interface Props {
    showViewCube?: boolean;
    showGrid?: boolean;
    showGroundShadow?: boolean;
    showDimensions: boolean;
    borderRadius?: string;
}

const ThreeJSCanvas = (props: Props) => {
    const material = QuickTurnBoltStore?.materials?.find(x => x.id === QuickTurnBoltStore?.material);
    const { bolt_head_diameter } = QuickTurnBoltStore;

    const { width } = useWindowSize();

    const [scene, setScene] = useState<THREE.Scene>();
    const [geometry, setGeometry] = useState<THREE.BufferGeometry>();
    const [showDimensions, setShowDimensions] = useState<boolean>(props.showDimensions);
    const [renamePartModalVisible, setRenamePartModalVisible] = useState<boolean>(false);

    const overallLengthInputRef = useRef();

    const loader = new THREE.BufferGeometryLoader();

    const gridConfig = {
        gridSize: [10.5, 10.5],
        cellSize: 0.5,
        cellThickness: 1,
        cellColor: '#6f6f6f',
        sectionSize: 5,
        sectionThickness: 1.5,
        sectionColor: '#7f7f7f',
        fadeDistance: 30,
        fadeStrength: 1,
        followCamera: false,
        infiniteGrid: true,
    };

    function Scene() {
        const threejs = useThree();
        setScene(threejs?.scene);

        return null;
    }

    useEffect(() => {
        if (width && width <= 480) {
            setShowDimensions(false);
        }
    }, [width]);

    useEffect(() => {
        if (QuickTurnBoltStore.bufferGeometry) {
            const bufferGeo = loader.parse(JSON.parse(QuickTurnBoltStore.bufferGeometry));
            setGeometry(bufferGeo);
        }
    }, [QuickTurnBoltStore.bufferGeometry]);

    return (
        <>
            {!QuickTurnBoltStore.boltToken ||
            !QuickTurnBoltStore.bufferGeometry ||
            !QuickTurnBoltStore.materials ||
            !material ? (
                <PortalLoading />
            ) : (
                <div id="quickturn-3d-canvas-container">
                    <div
                        className="mb-2"
                        style={{
                            position: 'relative',
                            paddingBottom: '75%',
                            zIndex: 0,
                            borderRadius: props?.borderRadius ? props.borderRadius : '0.25rem',
                            // background: 'radial-gradient(circle, rgba(82,89,94,1) 0%, rgba(46,53,60,1) 100%)',
                            background: '#ffffff',
                            border: '1px solid #e0e0e0',
                        }}
                    >
                        <div
                            className="d-flex align-items-center justify-content-end"
                            style={{
                                columnGap: '6px',
                                position: 'absolute',
                                padding: '1.25rem',
                                zIndex: 9,
                                userSelect: 'none',
                            }}
                        >
                            <Icon component={ThreeD} style={{ fontSize: '1.5rem' }} />
                            <span style={{ fontWeight: 500, fontSize: '1rem' }}>3D Preview</span>
                            {/* <Switch
                                size="small"
                                checked={showDimensions}
                                onChange={value => setShowDimensions(value)}
                            />
                            <span style={{ color: '#ccc', userSelect: 'none' }}>Show Dimensions</span> */}
                        </div>
                        <Suspense fallback={<Loader />}>
                            <Canvas
                                dpr={window.devicePixelRatio}
                                camera={{ fov: 18, near: 0.1, far: 1000, position: [6, 3, -6] }}
                                shadows={true}
                                className="rounded-sm"
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                }}
                            >
                                <Suspense fallback={null}>
                                    <Scene />

                                    <>
                                        {width && width > 480 && props?.showViewCube !== false ? (
                                            <Suspense fallback={null}>
                                                <Viewcube />
                                            </Suspense>
                                        ) : null}

                                        <hemisphereLight intensity={0.5} position={[0, 200, 0]} />
                                        <directionalLight
                                            color={0xbbbbbb}
                                            intensity={0.5}
                                            position={[6, 50, -12]}
                                            // shadow={[200, 200, 200]}
                                        />

                                        <Environment files="/hdr/empty_warehouse_01_1k.hdr" />

                                        <pointLight position={[10, 10, 10]} intensity={0.5} />
                                        <Center>
                                            <group>
                                                <mesh geometry={geometry} castShadow>
                                                    <meshPhysicalMaterial
                                                        attach="material"
                                                        color={0xcccccc}
                                                        metalness={1}
                                                        roughness={0.2}
                                                    />
                                                    <Edges
                                                        scale={1.0}
                                                        threshold={15} // Display edges only when the angle between two faces exceeds this value (default=15 degrees)
                                                        color={0x333333}
                                                    />
                                                </mesh>
                                            </group>
                                        </Center>

                                        {props?.showGrid !== false ? (
                                            <>
                                                <Grid
                                                    position={[0, -bolt_head_diameter / 2 - 0.01, 0]}
                                                    {...gridConfig}
                                                />
                                            </>
                                        ) : null}
                                        {props?.showGroundShadow !== false ? (
                                            <>
                                                <ContactShadows
                                                    position={[0, -bolt_head_diameter / 2 - 0.01, 0]}
                                                    opacity={0.15}
                                                    scale={5}
                                                    blur={2}
                                                />
                                            </>
                                        ) : null}

                                        <OrbitControls
                                            dampingFactor={0.3}
                                            minDistance={2}
                                            maxDistance={15}
                                            // minPolarAngle={0}
                                            // maxPolarAngle={-90}
                                            // enableZoom={false}
                                            // enablePan={false}
                                        />
                                    </>
                                </Suspense>
                            </Canvas>
                            {/* <Loader /> */}
                        </Suspense>
                    </div>
                </div>
            )}
        </>
    );
};

export default observer(ThreeJSCanvas);
